/**
 * Roles Store (Zustand)
 *
 * Manages the global state for user role definitions:
 * - Role definitions from Firestore
 * - Loading and error states
 * - Helper functions for role lookups
 *
 * This store is populated by useRolesListener and consumed by
 * usePagePermissions for role-based permission resolution.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { RoleDefinition, RoleDepartment } from '../types/roles.types';

interface RolesState {
  /** All role definitions from Firestore */
  roles: RoleDefinition[];

  /** Whether roles are currently being loaded */
  isLoading: boolean;

  /** Error message if role loading failed */
  error: string | null;

  /** Whether initial load has completed */
  isInitialized: boolean;

  // Actions
  setRoles: (roles: RoleDefinition[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;

  // Selectors (computed values)
  getRoleById: (roleId: string) => RoleDefinition | undefined;
  getRolesByDepartment: (department: RoleDepartment) => RoleDefinition[];
  getActiveRoles: () => RoleDefinition[];
  getActiveRolesByDepartment: (department: RoleDepartment) => RoleDefinition[];
}

export const useRolesStore = create<RolesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      roles: [],
      isLoading: true,
      error: null,
      isInitialized: false,

      // Actions
      setRoles: (roles) => set({ roles, isLoading: false, error: null }),
      setLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error, isLoading: false }),
      setInitialized: (initialized) => set({ isInitialized: initialized }),

      // Selectors
      getRoleById: (roleId) => {
        return get().roles.find((role) => role.id === roleId);
      },

      getRolesByDepartment: (department) => {
        return get()
          .roles.filter((role) => role.department === department)
          .sort((a, b) => a.sortOrder - b.sortOrder);
      },

      getActiveRoles: () => {
        return get()
          .roles.filter((role) => role.isActive)
          .sort((a, b) => {
            // Sort by department first, then by sortOrder
            if (a.department !== b.department) {
              return a.department.localeCompare(b.department);
            }
            return a.sortOrder - b.sortOrder;
          });
      },

      getActiveRolesByDepartment: (department) => {
        return get()
          .roles.filter((role) => role.department === department && role.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);
      },
    }),
    { name: 'RolesStore' }
  )
);

/**
 * Helper to check if a role grants access to a specific page
 */
export function roleGrantsPageAccess(
  role: RoleDefinition,
  pageId: string
): boolean {
  return pageId in role.pagePermissions;
}

/**
 * Helper to check if a role grants editor access to a specific page
 */
export function roleGrantsEditorAccess(
  role: RoleDefinition,
  pageId: string
): boolean {
  const permission = role.pagePermissions[pageId];
  return permission?.accessLevel === 'editor';
}

/**
 * Helper to get the access level for a page from a role
 */
export function getRoleAccessLevel(
  role: RoleDefinition,
  pageId: string
): 'editor' | 'viewer' | null {
  const permission = role.pagePermissions[pageId];
  return permission?.accessLevel ?? null;
}
